"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateBatchReportPDF = exports.generateStudentDossierPDF = exports.setGradeReportConfig = void 0;
const jspdf_1 = __importDefault(require("jspdf"));
const jspdf_autotable_1 = __importDefault(require("jspdf-autotable"));
const config_1 = require("./config");
var config_2 = require("./config");
Object.defineProperty(exports, "setGradeReportConfig", { enumerable: true, get: function () { return config_2.setConfig; } });
/**
 * Helper function to filter test data based on provided filters
 */
const filterTests = (tests, filters) => {
    if (!tests || tests.length === 0)
        return tests;
    if (!filters)
        return tests;
    return tests.filter(test => {
        // Filter by category
        if (filters.testCategories && filters.testCategories.length > 0) {
            if (!filters.testCategories.includes(test.testCategoryName)) {
                return false;
            }
        }
        // Note: Test data doesn't have date field in current model
        // If dates are added later, implement filtering here:
        // if (filters.testStartDate && test.date < filters.testStartDate) return false;
        // if (filters.testEndDate && test.date > filters.testEndDate) return false;
        return true;
    });
};
/**
 * Helper function to filter attendance data based on provided filters
 */
const filterAttendance = (attendance, filters) => {
    if (!attendance || attendance.length === 0)
        return attendance;
    if (!filters)
        return attendance;
    return attendance.filter(att => {
        // Filter by date range (Year + Month)
        if (filters.attendanceStartDate || filters.attendanceEndDate) {
            // Parse attendance year/month into a comparable format
            const monthMap = {
                'January': 1, 'February': 2, 'March': 3, 'April': 4,
                'May': 5, 'June': 6, 'July': 7, 'August': 8,
                'September': 9, 'October': 10, 'November': 11, 'December': 12
            };
            const attMonth = monthMap[att.Month] || 1;
            const attDate = new Date(att.Year, attMonth - 1, 1);
            if (filters.attendanceStartDate) {
                const startDate = new Date(filters.attendanceStartDate);
                if (attDate < startDate)
                    return false;
            }
            if (filters.attendanceEndDate) {
                const endDate = new Date(filters.attendanceEndDate);
                if (attDate > endDate)
                    return false;
            }
        }
        return true;
    });
};
/**
 * Helper function to apply filters to a student record and return filtered copy
 */
const applyFiltersToStudent = (student, filters) => {
    if (!filters)
        return student;
    return {
        ...student,
        TestInfo: filterTests(student.TestInfo, filters),
        AttendanceInfo: filterAttendance(student.AttendanceInfo, filters)
    };
};
/**
 * Helper function to check if student has enough tests for ranking
 */
const hasEnoughTestsForRanking = (student, minTests = 5) => {
    const testCount = student.TestInfo?.length || 0;
    return testCount >= minTests;
};
/**
 * Helper function to get rank display value
 */
const getRankDisplay = (student, totalStudents, minTests = 5) => {
    if (!hasEnoughTestsForRanking(student, minTests)) {
        return 'Insufficient Data';
    }
    return `#${student.rank} of ${totalStudents}`;
};
/**
 * Generate a comprehensive student dossier PDF with marks and attendance data
 * @param student - Student data with scores and attendance
 * @param options - Configuration including preset style
 */
const generateStudentDossierPDF = (student, options) => {
    const preset = options.preset || 'detailed';
    // Apply filters to student data
    const filteredStudent = applyFiltersToStudent(student, options.filters);
    switch (preset) {
        case 'grouped-by-category':
            generateGroupedByCategoryPDF(filteredStudent, options);
            break;
        case 'summary':
            generateSummaryPDF(filteredStudent, options);
            break;
        case 'compact':
            generateCompactPDF(filteredStudent, options);
            break;
        case 'detailed':
        default:
            generateDetailedPDF(filteredStudent, options);
            break;
    }
};
exports.generateStudentDossierPDF = generateStudentDossierPDF;
/**
 * PRESET 1: Detailed - All tests in chronological order (original format)
 */
const generateDetailedPDF = (student, options) => {
    const doc = new jspdf_1.default();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;
    // Header with school branding
    doc.setFillColor(41, 128, 185);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('STUDENT ACADEMIC DOSSIER', pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on ${new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })}`, pageWidth / 2, 25, { align: 'center' });
    yPosition = 45;
    // Student Information Section
    doc.setTextColor(0, 0, 0);
    doc.setFillColor(240, 240, 240);
    doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('STUDENT INFORMATION', 15, yPosition + 7);
    yPosition += 15;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const studentInfo = [
        ['Full Name:', student.StudentInfo.FullName],
        ['Email:', student.StudentInfo.Email],
        ['Course:', options.courseName],
        ['Batch:', `${options.batchName} (${options.batchCode})`],
        ['Academic Year:', student.StudentInfo.YearName || 'N/A'],
    ];
    studentInfo.forEach(([label, value]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(label, 15, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(value, 60, yPosition);
        yPosition += 7;
    });
    yPosition += 5;
    // Performance Summary Section
    doc.setFillColor(240, 240, 240);
    doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('PERFORMANCE SUMMARY', 15, yPosition + 7);
    yPosition += 15;
    // Performance metrics in colored boxes
    const minTestsForRanking = options.filters?.minTestsForRanking || 5;
    const metrics = [
        {
            label: 'Class Rank',
            value: getRankDisplay(student, options.totalStudents, minTestsForRanking),
            color: getRankColor(student.rank, options.totalStudents)
        },
        {
            label: 'Average Score',
            value: `${student.averageScore.toFixed(2)}%`,
            color: getScoreColor(student.averageScore)
        },
        {
            label: 'Attendance',
            value: `${student.attendancePercentage.toFixed(2)}%`,
            color: getAttendanceColor(student.attendancePercentage)
        },
    ];
    const boxWidth = (pageWidth - 35) / 3;
    let xPos = 15;
    metrics.forEach((metric) => {
        doc.setFillColor(...metric.color);
        doc.roundedRect(xPos, yPosition, boxWidth, 20, 2, 2, 'F');
        // Use dark text for light backgrounds (yellow/gold), white text for dark backgrounds
        const isLightBackground = metric.color[0] > 200 && metric.color[1] > 200;
        if (isLightBackground) {
            doc.setTextColor(0, 0, 0); // Black text
        }
        else {
            doc.setTextColor(255, 255, 255); // White text
        }
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(metric.label, xPos + boxWidth / 2, yPosition + 7, { align: 'center' });
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(metric.value, xPos + boxWidth / 2, yPosition + 15, { align: 'center' });
        xPos += boxWidth + 2.5;
    });
    yPosition += 30;
    doc.setTextColor(0, 0, 0);
    // Test Results Section
    if (student.TestInfo && student.TestInfo.length > 0) {
        doc.setFillColor(240, 240, 240);
        doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('TEST RESULTS', 15, yPosition + 7);
        yPosition += 15;
        const testData = student.TestInfo.map((test, index) => {
            const percentage = (test.totalScored / test.totalMaxMarks) * 100;
            return [
                (index + 1).toString(),
                test.testName || 'N/A',
                test.testCategoryName || 'N/A',
                `${test.totalScored.toFixed(1)}`,
                `${test.totalMaxMarks}`,
                `${percentage.toFixed(2)}%`,
                getPerformanceLabel(percentage)
            ];
        });
        (0, jspdf_autotable_1.default)(doc, {
            startY: yPosition,
            head: [['#', 'Test Name', 'Category', 'Scored', 'Max Marks', 'Percentage', 'Performance']],
            body: testData,
            theme: 'grid',
            headStyles: {
                fillColor: [52, 152, 219],
                textColor: 255,
                fontStyle: 'bold',
                fontSize: 9
            },
            bodyStyles: { fontSize: 8 },
            columnStyles: {
                0: { cellWidth: 10, halign: 'center' },
                1: { cellWidth: 45 },
                2: { cellWidth: 25 },
                3: { cellWidth: 20, halign: 'right' },
                4: { cellWidth: 25, halign: 'right' },
                5: { cellWidth: 25, halign: 'right' },
                6: { cellWidth: 30, halign: 'center' },
            },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            didParseCell: (data) => {
                if (data.section === 'body' && data.column.index === 6) {
                    const percentage = parseFloat(testData[data.row.index][5]);
                    if (percentage >= 75) {
                        data.cell.styles.textColor = [16, 185, 129];
                        data.cell.styles.fontStyle = 'bold';
                    }
                    else if (percentage < 50) {
                        data.cell.styles.textColor = [239, 68, 68];
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            }
        });
        yPosition = doc.lastAutoTable.finalY + 10;
    }
    // Attendance Section
    if (student.AttendanceInfo && student.AttendanceInfo.length > 0) {
        // Check if we need a new page
        if (yPosition > pageHeight - 80) {
            doc.addPage();
            yPosition = 20;
        }
        doc.setFillColor(240, 240, 240);
        doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('ATTENDANCE RECORD', 15, yPosition + 7);
        yPosition += 15;
        const attendanceData = student.AttendanceInfo.map((att, index) => {
            const percentage = att.TotalClasses > 0 ? (att.Presents / att.TotalClasses) * 100 : 0;
            return [
                (index + 1).toString(),
                att.TopicName || 'N/A',
                att.Presents.toString(),
                att.Absents.toString(),
                att.TotalClasses.toString(),
                `${percentage.toFixed(2)}%`,
                getAttendanceLabel(percentage)
            ];
        });
        (0, jspdf_autotable_1.default)(doc, {
            startY: yPosition,
            head: [['#', 'Topic', 'Present', 'Absent', 'Total Classes', 'Percentage', 'Status']],
            body: attendanceData,
            theme: 'grid',
            headStyles: {
                fillColor: [52, 152, 219],
                textColor: 255,
                fontStyle: 'bold',
                fontSize: 9
            },
            bodyStyles: { fontSize: 8 },
            columnStyles: {
                0: { cellWidth: 10, halign: 'center' },
                1: { cellWidth: 50 },
                2: { cellWidth: 20, halign: 'center' },
                3: { cellWidth: 20, halign: 'center' },
                4: { cellWidth: 25, halign: 'center' },
                5: { cellWidth: 25, halign: 'right' },
                6: { cellWidth: 30, halign: 'center' },
            },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            didParseCell: (data) => {
                if (data.section === 'body' && data.column.index === 6) {
                    const percentage = parseFloat(attendanceData[data.row.index][5]);
                    if (percentage >= 75) {
                        data.cell.styles.textColor = [16, 185, 129];
                        data.cell.styles.fontStyle = 'bold';
                    }
                    else if (percentage < 60) {
                        data.cell.styles.textColor = [239, 68, 68];
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            }
        });
        yPosition = doc.lastAutoTable.finalY + 10;
    }
    // Footer on last page
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        doc.text('This is a confidential document', pageWidth - 15, pageHeight - 10, { align: 'right' });
    }
    // Generate filename
    const filename = `${student.StudentInfo.FullName.replace(/\s+/g, '_')}_Dossier_Detailed_${new Date().toISOString().split('T')[0]}.pdf`;
    // Save the PDF
    doc.save(filename);
};
/**
 * PRESET 2: Grouped by Category - Tests organized by category with subtotals
 */
const generateGroupedByCategoryPDF = (student, options) => {
    const doc = new jspdf_1.default();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;
    // Header (same as detailed)
    renderPDFHeader(doc, pageWidth);
    yPosition = 45;
    // Student Information Section (same as detailed)
    renderStudentInfo(doc, student, options, pageWidth, yPosition);
    yPosition += 50;
    // Performance Summary Section (same as detailed)
    renderPerformanceSummary(doc, student, options, pageWidth, yPosition);
    yPosition += 35;
    // Test Results Section - GROUPED BY CATEGORY
    if (student.TestInfo && student.TestInfo.length > 0) {
        doc.setFillColor(240, 240, 240);
        doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text('TEST RESULTS - GROUPED BY CATEGORY', 15, yPosition + 7);
        yPosition += 15;
        // Group tests by category
        const testsByCategory = {};
        student.TestInfo.forEach((test) => {
            const category = test.testCategoryName || 'Uncategorized';
            if (!testsByCategory[category]) {
                testsByCategory[category] = [];
            }
            testsByCategory[category].push(test);
        });
        // Render each category
        Object.entries(testsByCategory).forEach(([category, tests], categoryIndex) => {
            // Check if we need a new page
            if (yPosition > pageHeight - 80) {
                doc.addPage();
                yPosition = 20;
            }
            // Category header
            doc.setFillColor(52, 152, 219);
            doc.rect(10, yPosition, pageWidth - 20, 8, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(`${category} (${tests.length} test${tests.length > 1 ? 's' : ''})`, 15, yPosition + 5.5);
            yPosition += 10;
            // Calculate category average
            const categoryAverage = tests.reduce((sum, test) => {
                return sum + (test.totalScored / test.totalMaxMarks) * 100;
            }, 0) / tests.length;
            // Test data for this category
            const testData = tests.map((test, index) => {
                const percentage = (test.totalScored / test.totalMaxMarks) * 100;
                return [
                    (index + 1).toString(),
                    test.testName || 'N/A',
                    `${test.totalScored.toFixed(1)}`,
                    `${test.totalMaxMarks}`,
                    `${percentage.toFixed(2)}%`,
                    getPerformanceLabel(percentage)
                ];
            });
            // Add category average row
            testData.push([
                '',
                'Category Average',
                '',
                '',
                `${categoryAverage.toFixed(2)}%`,
                getPerformanceLabel(categoryAverage)
            ]);
            (0, jspdf_autotable_1.default)(doc, {
                startY: yPosition,
                head: [['#', 'Test Name', 'Scored', 'Max Marks', 'Percentage', 'Performance']],
                body: testData,
                theme: 'grid',
                headStyles: {
                    fillColor: [71, 171, 238],
                    textColor: 255,
                    fontStyle: 'bold',
                    fontSize: 9
                },
                bodyStyles: { fontSize: 8 },
                columnStyles: {
                    0: { cellWidth: 12, halign: 'center' },
                    1: { cellWidth: 65 },
                    2: { cellWidth: 20, halign: 'right' },
                    3: { cellWidth: 25, halign: 'right' },
                    4: { cellWidth: 25, halign: 'right' },
                    5: { cellWidth: 30, halign: 'center' },
                },
                alternateRowStyles: { fillColor: [245, 245, 245] },
                didParseCell: (data) => {
                    // Highlight category average row
                    if (data.section === 'body' && data.row.index === testData.length - 1) {
                        data.cell.styles.fillColor = [230, 245, 255];
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.textColor = [52, 152, 219];
                    }
                    // Color code performance
                    if (data.section === 'body' && data.column.index === 5) {
                        const percentage = parseFloat(testData[data.row.index][4]);
                        if (percentage >= 75) {
                            data.cell.styles.textColor = [16, 185, 129];
                            data.cell.styles.fontStyle = 'bold';
                        }
                        else if (percentage < 50) {
                            data.cell.styles.textColor = [239, 68, 68];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                }
            });
            yPosition = doc.lastAutoTable.finalY + 8;
        });
    }
    // Attendance Section (same as detailed)
    renderAttendanceSection(doc, student, pageWidth, pageHeight, yPosition);
    // Footer
    renderPDFFooter(doc, pageWidth, pageHeight);
    // Generate filename
    const filename = `${student.StudentInfo.FullName.replace(/\s+/g, '_')}_Dossier_Grouped_${new Date().toISOString().split('T')[0]}.pdf`;
    // Save the PDF
    doc.save(filename);
};
/**
 * PRESET 3: Summary - High-level overview with category averages only
 */
const generateSummaryPDF = (student, options) => {
    const doc = new jspdf_1.default();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;
    // Header
    renderPDFHeader(doc, pageWidth);
    yPosition = 45;
    // Student Information
    renderStudentInfo(doc, student, options, pageWidth, yPosition);
    yPosition += 50;
    // Performance Summary
    renderPerformanceSummary(doc, student, options, pageWidth, yPosition);
    yPosition += 35;
    // Category Averages Only (no individual tests)
    if (student.TestInfo && student.TestInfo.length > 0) {
        doc.setFillColor(240, 240, 240);
        doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text('ACADEMIC PERFORMANCE BY CATEGORY', 15, yPosition + 7);
        yPosition += 15;
        // Group tests by category and calculate averages
        const testsByCategory = {};
        student.TestInfo.forEach((test) => {
            const category = test.testCategoryName || 'Uncategorized';
            if (!testsByCategory[category]) {
                testsByCategory[category] = [];
            }
            testsByCategory[category].push(test);
        });
        const categoryData = Object.entries(testsByCategory).map(([category, tests]) => {
            const average = tests.reduce((sum, test) => {
                return sum + (test.totalScored / test.totalMaxMarks) * 100;
            }, 0) / tests.length;
            return [
                category,
                tests.length.toString(),
                `${average.toFixed(2)}%`,
                getPerformanceLabel(average)
            ];
        });
        (0, jspdf_autotable_1.default)(doc, {
            startY: yPosition,
            head: [['Test Category', 'Tests Taken', 'Average Score', 'Performance']],
            body: categoryData,
            theme: 'grid',
            headStyles: {
                fillColor: [52, 152, 219],
                textColor: 255,
                fontStyle: 'bold',
                fontSize: 10
            },
            bodyStyles: { fontSize: 9 },
            columnStyles: {
                0: { cellWidth: 70 },
                1: { cellWidth: 30, halign: 'center' },
                2: { cellWidth: 40, halign: 'right' },
                3: { cellWidth: 37, halign: 'center' },
            },
            didParseCell: (data) => {
                if (data.section === 'body' && data.column.index === 3) {
                    const percentage = parseFloat(categoryData[data.row.index][2]);
                    if (percentage >= 75) {
                        data.cell.styles.textColor = [16, 185, 129];
                        data.cell.styles.fontStyle = 'bold';
                    }
                    else if (percentage < 50) {
                        data.cell.styles.textColor = [239, 68, 68];
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            }
        });
        yPosition = doc.lastAutoTable.finalY + 10;
    }
    // Attendance Summary (simplified)
    if (student.AttendanceInfo && student.AttendanceInfo.length > 0) {
        doc.setFillColor(240, 240, 240);
        doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('ATTENDANCE SUMMARY', 15, yPosition + 7);
        yPosition += 15;
        const totalClasses = student.AttendanceInfo.reduce((t, a) => t + a.TotalClasses, 0);
        const totalPresent = student.AttendanceInfo.reduce((t, a) => t + a.Presents, 0);
        const totalAbsent = student.AttendanceInfo.reduce((t, a) => t + a.Absents, 0);
        const overallPercentage = totalClasses > 0 ? (totalPresent / totalClasses) * 100 : 0;
        const summaryData = [
            ['Total Classes', totalClasses.toString()],
            ['Classes Attended', totalPresent.toString()],
            ['Classes Missed', totalAbsent.toString()],
            ['Attendance Rate', `${overallPercentage.toFixed(2)}%`],
            ['Status', getAttendanceLabel(overallPercentage)]
        ];
        (0, jspdf_autotable_1.default)(doc, {
            startY: yPosition,
            body: summaryData,
            theme: 'grid',
            columnStyles: {
                0: { cellWidth: 80, fontStyle: 'bold' },
                1: { cellWidth: 97, halign: 'right', fontSize: 10 }
            },
            didParseCell: (data) => {
                if (data.row.index === 4 && data.column.index === 1) {
                    if (overallPercentage >= 75) {
                        data.cell.styles.textColor = [16, 185, 129];
                        data.cell.styles.fontStyle = 'bold';
                    }
                    else if (overallPercentage < 60) {
                        data.cell.styles.textColor = [239, 68, 68];
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            }
        });
    }
    // Footer
    renderPDFFooter(doc, pageWidth, pageHeight);
    const filename = `${student.StudentInfo.FullName.replace(/\s+/g, '_')}_Dossier_Summary_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
};
/**
 * PRESET 4: Compact - Single page overview
 */
const generateCompactPDF = (student, options) => {
    const doc = new jspdf_1.default();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 15;
    // Compact header
    doc.setFillColor(52, 152, 219);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('STUDENT DOSSIER', pageWidth / 2, 12, { align: 'center' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`${new Date().toLocaleDateString()}`, pageWidth / 2, 19, { align: 'center' });
    yPosition = 30;
    // Compact student info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(student.StudentInfo.FullName, 15, yPosition);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`${options.courseName} • ${options.batchName}`, 15, yPosition + 5);
    yPosition += 12;
    // Performance metrics (compact boxes)
    const metrics = [
        { label: 'Rank', value: `#${student.rank}/${options.totalStudents}` },
        { label: 'Score', value: `${student.averageScore.toFixed(1)}%` },
        { label: 'Attendance', value: `${student.attendancePercentage.toFixed(1)}%` }
    ];
    const boxWidth = (pageWidth - 35) / 3;
    let xPos = 15;
    metrics.forEach((metric) => {
        doc.setDrawColor(200, 200, 200);
        doc.rect(xPos, yPosition, boxWidth, 15);
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.text(metric.label, xPos + 2, yPosition + 5);
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.text(metric.value, xPos + 2, yPosition + 12);
        doc.setFont('helvetica', 'normal');
        xPos += boxWidth + 2.5;
    });
    yPosition += 20;
    // Compact test categories
    if (student.TestInfo && student.TestInfo.length > 0) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Test Performance', 15, yPosition);
        yPosition += 5;
        const testsByCategory = {};
        student.TestInfo.forEach((test) => {
            const category = test.testCategoryName || 'Tests';
            if (!testsByCategory[category])
                testsByCategory[category] = [];
            testsByCategory[category].push(test);
        });
        const categoryData = Object.entries(testsByCategory).map(([category, tests]) => {
            const avg = tests.reduce((s, t) => s + (t.totalScored / t.totalMaxMarks) * 100, 0) / tests.length;
            return [category, tests.length, `${avg.toFixed(1)}%`];
        });
        (0, jspdf_autotable_1.default)(doc, {
            startY: yPosition,
            head: [['Category', 'Tests', 'Avg']],
            body: categoryData,
            theme: 'plain',
            headStyles: { fillColor: [240, 240, 240], textColor: 0, fontSize: 7 },
            bodyStyles: { fontSize: 7 },
            columnStyles: {
                0: { cellWidth: 100 },
                1: { cellWidth: 25, halign: 'center' },
                2: { cellWidth: 30, halign: 'right' }
            },
            margin: { left: 15, right: 15 }
        });
        yPosition = doc.lastAutoTable.finalY + 5;
    }
    // Compact attendance
    if (student.AttendanceInfo && student.AttendanceInfo.length > 0) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Attendance', 15, yPosition);
        yPosition += 3;
        const total = student.AttendanceInfo.reduce((t, a) => t + a.TotalClasses, 0);
        const present = student.AttendanceInfo.reduce((t, a) => t + a.Presents, 0);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(`${present} of ${total} classes attended`, 15, yPosition + 3);
    }
    // Footer
    doc.setFontSize(6);
    doc.setTextColor(128, 128, 128);
    doc.text('Confidential', pageWidth / 2, pageHeight - 10, { align: 'center' });
    const filename = `${student.StudentInfo.FullName.replace(/\s+/g, '_')}_Dossier_Compact_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
};
// Helper functions
const getRankColor = (rank, total) => {
    const percentage = (rank / total) * 100;
    if (percentage <= 10)
        return [255, 215, 0]; // Gold
    if (percentage <= 25)
        return [192, 192, 192]; // Silver
    if (percentage <= 50)
        return [205, 127, 50]; // Bronze
    return [100, 100, 100]; // Default gray
};
const getScoreColor = (score) => {
    if (score >= 75)
        return [16, 185, 129]; // Green
    if (score >= 50)
        return [245, 158, 11]; // Orange
    return [239, 68, 68]; // Red
};
const getAttendanceColor = (percentage) => {
    if (percentage >= 75)
        return [16, 185, 129]; // Green
    if (percentage >= 60)
        return [245, 158, 11]; // Orange
    return [239, 68, 68]; // Red
};
const getPerformanceLabel = (percentage) => {
    if (percentage >= 90)
        return 'Excellent';
    if (percentage >= 75)
        return 'Good';
    if (percentage >= 50)
        return 'Average';
    if (percentage >= 35)
        return 'Below Avg';
    return 'Poor';
};
const getAttendanceLabel = (percentage) => {
    if (percentage >= 90)
        return 'Excellent';
    if (percentage >= 75)
        return 'Good';
    if (percentage >= 60)
        return 'Satisfactory';
    return 'Poor';
};
// ========== Shared Rendering Functions ==========
const renderPDFHeader = (doc, pageWidth) => {
    doc.setFillColor(41, 128, 185);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('STUDENT ACADEMIC DOSSIER', pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on ${new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })}`, pageWidth / 2, 25, { align: 'center' });
};
const renderStudentInfo = (doc, student, options, pageWidth, yPosition) => {
    doc.setTextColor(0, 0, 0);
    doc.setFillColor(240, 240, 240);
    doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('STUDENT INFORMATION', 15, yPosition + 7);
    yPosition += 15;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const studentInfo = [
        ['Full Name:', student.StudentInfo.FullName],
        ['Email:', student.StudentInfo.Email],
        ['Course:', options.courseName],
        ['Batch:', `${options.batchName} (${options.batchCode})`],
        ['Academic Year:', student.StudentInfo.YearName || 'N/A'],
    ];
    studentInfo.forEach(([label, value]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(label, 15, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(value, 60, yPosition);
        yPosition += 7;
    });
};
const renderPerformanceSummary = (doc, student, options, pageWidth, yPosition) => {
    doc.setTextColor(0, 0, 0);
    doc.setFillColor(240, 240, 240);
    doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('PERFORMANCE SUMMARY', 15, yPosition + 7);
    yPosition += 15;
    const minTestsForRanking = options.filters?.minTestsForRanking || 5;
    const metrics = [
        {
            label: 'Class Rank',
            value: getRankDisplay(student, options.totalStudents, minTestsForRanking),
            color: getRankColor(student.rank, options.totalStudents)
        },
        {
            label: 'Average Score',
            value: `${student.averageScore.toFixed(2)}%`,
            color: getScoreColor(student.averageScore)
        },
        {
            label: 'Attendance',
            value: `${student.attendancePercentage.toFixed(2)}%`,
            color: getAttendanceColor(student.attendancePercentage)
        },
    ];
    const boxWidth = (pageWidth - 35) / 3;
    let xPos = 15;
    metrics.forEach((metric) => {
        doc.setFillColor(...metric.color);
        doc.roundedRect(xPos, yPosition, boxWidth, 20, 2, 2, 'F');
        // Use dark text for light backgrounds (yellow/gold), white text for dark backgrounds
        const isLightBackground = metric.color[0] > 200 && metric.color[1] > 200;
        if (isLightBackground) {
            doc.setTextColor(0, 0, 0);
        }
        else {
            doc.setTextColor(255, 255, 255);
        }
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(metric.label, xPos + boxWidth / 2, yPosition + 7, { align: 'center' });
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(metric.value, xPos + boxWidth / 2, yPosition + 15, { align: 'center' });
        xPos += boxWidth + 2.5;
    });
    doc.setTextColor(0, 0, 0);
};
const renderAttendanceSection = (doc, student, pageWidth, pageHeight, yPosition) => {
    if (student.AttendanceInfo && student.AttendanceInfo.length > 0) {
        if (yPosition > pageHeight - 80) {
            doc.addPage();
            yPosition = 20;
        }
        doc.setFillColor(240, 240, 240);
        doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text('ATTENDANCE RECORD', 15, yPosition + 7);
        yPosition += 15;
        const attendanceData = student.AttendanceInfo.map((att, index) => {
            const percentage = att.TotalClasses > 0 ? (att.Presents / att.TotalClasses) * 100 : 0;
            return [
                (index + 1).toString(),
                att.TopicName || 'N/A',
                att.Presents.toString(),
                att.Absents.toString(),
                att.TotalClasses.toString(),
                `${percentage.toFixed(2)}%`,
                getAttendanceLabel(percentage)
            ];
        });
        (0, jspdf_autotable_1.default)(doc, {
            startY: yPosition,
            head: [['#', 'Topic', 'Present', 'Absent', 'Total Classes', 'Percentage', 'Status']],
            body: attendanceData,
            theme: 'grid',
            headStyles: {
                fillColor: [52, 152, 219],
                textColor: 255,
                fontStyle: 'bold',
                fontSize: 9
            },
            bodyStyles: { fontSize: 8 },
            columnStyles: {
                0: { cellWidth: 10, halign: 'center' },
                1: { cellWidth: 50 },
                2: { cellWidth: 20, halign: 'center' },
                3: { cellWidth: 20, halign: 'center' },
                4: { cellWidth: 25, halign: 'center' },
                5: { cellWidth: 25, halign: 'right' },
                6: { cellWidth: 30, halign: 'center' },
            },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            didParseCell: (data) => {
                if (data.section === 'body' && data.column.index === 6) {
                    const percentage = parseFloat(attendanceData[data.row.index][5]);
                    if (percentage >= 75) {
                        data.cell.styles.textColor = [16, 185, 129];
                        data.cell.styles.fontStyle = 'bold';
                    }
                    else if (percentage < 60) {
                        data.cell.styles.textColor = [239, 68, 68];
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            }
        });
    }
};
const renderPDFFooter = (doc, pageWidth, pageHeight) => {
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        doc.text('This is a confidential document', pageWidth - 15, pageHeight - 10, { align: 'right' });
    }
};
// ========== BATCH-LEVEL PDF GENERATION ==========
/**
 * Generate a batch-level PDF report with all students
 */
const generateBatchReportPDF = async (students, options) => {
    const preset = options.preset || 'overview';
    // Apply filters to all students (for both hard-coded and database templates)
    const filteredStudents = students.map(student => applyFiltersToStudent(student, options.filters));
    // Check if it's a database template (format: "db-template-<id>")
    if (preset.startsWith('db-template-')) {
        const templateId = preset.replace('db-template-', '');
        console.log(`[generateBatchReportPDF] Detected database template with ID: ${templateId}`);
        return generateReportFromDatabaseTemplate(templateId, filteredStudents, options);
    }
    switch (preset) {
        case 'detailed-list':
            generateBatchDetailedListPDF(filteredStudents, options);
            break;
        case 'analytics':
            generateBatchAnalyticsPDF(filteredStudents, options);
            break;
        case 'ptm':
            generateBatchPTMDossiersPDF(filteredStudents, options);
            break;
        case 'overview':
        default:
            generateBatchOverviewPDF(filteredStudents, options);
            break;
    }
};
exports.generateBatchReportPDF = generateBatchReportPDF;
/**
 * BATCH PRESET 1: Overview - Batch statistics and student ranking
 */
const generateBatchOverviewPDF = (students, options) => {
    const doc = new jspdf_1.default();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;
    // Header
    doc.setFillColor(41, 128, 185);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('BATCH PERFORMANCE REPORT', pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`${options.batchName} • ${options.courseName}`, pageWidth / 2, 25, { align: 'center' });
    yPosition = 45;
    // Batch Information
    doc.setTextColor(0, 0, 0);
    doc.setFillColor(240, 240, 240);
    doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('BATCH INFORMATION', 15, yPosition + 7);
    yPosition += 15;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const batchInfo = [
        ['Batch Name:', options.batchName],
        ['Batch Code:', options.batchCode],
        ['Course:', options.courseName],
        ['Total Students:', students.length.toString()],
        ['Report Date:', new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })],
    ];
    batchInfo.forEach(([label, value]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(label, 15, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(value, 60, yPosition);
        yPosition += 7;
    });
    yPosition += 10;
    // Batch Statistics
    const avgScore = students.reduce((sum, s) => sum + s.averageScore, 0) / students.length;
    const avgAttendance = students.reduce((sum, s) => sum + s.attendancePercentage, 0) / students.length;
    const atRiskCount = students.filter(s => s.averageScore < 40).length;
    const toppersCount = Math.ceil(students.length * 0.1);
    doc.setFillColor(240, 240, 240);
    doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('BATCH STATISTICS', 15, yPosition + 7);
    yPosition += 15;
    const statsBoxes = [
        { label: 'Avg Score', value: `${avgScore.toFixed(2)}%`, color: getScoreColor(avgScore) },
        { label: 'Avg Attendance', value: `${avgAttendance.toFixed(2)}%`, color: getAttendanceColor(avgAttendance) },
        { label: 'Top 10%', value: toppersCount.toString(), color: [16, 185, 129] },
        { label: 'At Risk', value: atRiskCount.toString(), color: [239, 68, 68] }
    ];
    const boxWidth = (pageWidth - 40) / 4;
    let xPos = 15;
    statsBoxes.forEach((box) => {
        doc.setFillColor(...box.color);
        doc.roundedRect(xPos, yPosition, boxWidth, 20, 2, 2, 'F');
        const isLightBackground = box.color[0] > 200 && box.color[1] > 200;
        doc.setTextColor(isLightBackground ? 0 : 255, isLightBackground ? 0 : 255, isLightBackground ? 0 : 255);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(box.label, xPos + boxWidth / 2, yPosition + 7, { align: 'center' });
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(box.value, xPos + boxWidth / 2, yPosition + 15, { align: 'center' });
        xPos += boxWidth + 2;
    });
    yPosition += 30;
    doc.setTextColor(0, 0, 0);
    // Student Rankings Table
    doc.setFillColor(240, 240, 240);
    doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('STUDENT RANKINGS', 15, yPosition + 7);
    yPosition += 15;
    const rankingsData = students.map((student) => [
        student.rank.toString(),
        student.StudentInfo.FullName,
        `${student.averageScore.toFixed(1)}%`,
        `${student.attendancePercentage.toFixed(1)}%`,
        getPerformanceLabel(student.averageScore)
    ]);
    (0, jspdf_autotable_1.default)(doc, {
        startY: yPosition,
        head: [['Rank', 'Student Name', 'Avg Score', 'Attendance', 'Performance']],
        body: rankingsData,
        theme: 'grid',
        headStyles: {
            fillColor: [52, 152, 219],
            textColor: 255,
            fontStyle: 'bold',
            fontSize: 9
        },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
            0: { cellWidth: 15, halign: 'center' },
            1: { cellWidth: 70 },
            2: { cellWidth: 25, halign: 'right' },
            3: { cellWidth: 25, halign: 'right' },
            4: { cellWidth: 30, halign: 'center' }
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        didParseCell: (data) => {
            if (data.section === 'body') {
                const rowIndex = data.row.index;
                const student = students[rowIndex];
                // Highlight top 10%
                if (student.rank <= toppersCount) {
                    data.cell.styles.fillColor = [240, 253, 244];
                }
                // Highlight at-risk students
                if (student.averageScore < 40) {
                    data.cell.styles.fillColor = [254, 242, 242];
                }
                // Color code performance
                if (data.column.index === 4) {
                    if (student.averageScore >= 75) {
                        data.cell.styles.textColor = [16, 185, 129];
                        data.cell.styles.fontStyle = 'bold';
                    }
                    else if (student.averageScore < 50) {
                        data.cell.styles.textColor = [239, 68, 68];
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            }
        }
    });
    // Footer
    renderPDFFooter(doc, pageWidth, pageHeight);
    const filename = `${options.batchName.replace(/\s+/g, '_')}_Batch_Report_Overview_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
};
/**
 * BATCH PRESET 2: Detailed List - All students with detailed stats
 */
const generateBatchDetailedListPDF = (students, options) => {
    const doc = new jspdf_1.default();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;
    // Header
    doc.setFillColor(41, 128, 185);
    doc.rect(0, 0, pageWidth, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('BATCH DETAILED REPORT', pageWidth / 2, 12, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`${options.batchName} • ${options.courseName}`, pageWidth / 2, 22, { align: 'center' });
    yPosition = 40;
    // Detailed student list with test counts and attendance
    students.forEach((student, index) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 60) {
            doc.addPage();
            yPosition = 20;
        }
        const testCount = student.TestInfo?.length || 0;
        const totalClasses = student.AttendanceInfo?.reduce((sum, a) => sum + a.TotalClasses, 0) || 0;
        const totalPresent = student.AttendanceInfo?.reduce((sum, a) => sum + a.Presents, 0) || 0;
        // Student card
        doc.setFillColor(249, 250, 251);
        doc.roundedRect(10, yPosition, pageWidth - 20, 35, 2, 2, 'F');
        // Rank badge
        const rankColor = getRankColor(student.rank, students.length);
        doc.setFillColor(...rankColor);
        doc.roundedRect(15, yPosition + 5, 25, 12, 2, 2, 'F');
        const isLightRank = rankColor[0] > 200 && rankColor[1] > 200;
        doc.setTextColor(isLightRank ? 0 : 255, isLightRank ? 0 : 255, isLightRank ? 0 : 255);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(`#${student.rank}`, 27.5, yPosition + 13, { align: 'center' });
        // Student name
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(student.StudentInfo.FullName, 45, yPosition + 10);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(student.StudentInfo.Email, 45, yPosition + 16);
        // Stats in grid
        const stats = [
            { label: 'Score', value: `${student.averageScore.toFixed(1)}%` },
            { label: 'Attendance', value: `${student.attendancePercentage.toFixed(1)}%` },
            { label: 'Tests', value: testCount.toString() },
            { label: 'Classes', value: `${totalPresent}/${totalClasses}` }
        ];
        let statX = 45;
        doc.setTextColor(0, 0, 0);
        stats.forEach((stat) => {
            doc.setFontSize(7);
            doc.setTextColor(100, 100, 100);
            doc.text(stat.label, statX, yPosition + 24);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(stat.value, statX, yPosition + 31);
            doc.setFont('helvetica', 'normal');
            statX += 35;
        });
        yPosition += 40;
    });
    // Footer
    renderPDFFooter(doc, pageWidth, pageHeight);
    const filename = `${options.batchName.replace(/\s+/g, '_')}_Batch_Report_Detailed_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
};
/**
 * BATCH PRESET 3: Analytics - Performance distribution and insights
 */
const generateBatchAnalyticsPDF = (students, options) => {
    const doc = new jspdf_1.default();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;
    // Header
    doc.setFillColor(41, 128, 185);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('BATCH ANALYTICS REPORT', pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`${options.batchName} • ${options.courseName}`, pageWidth / 2, 25, { align: 'center' });
    yPosition = 45;
    // Performance Distribution
    doc.setTextColor(0, 0, 0);
    doc.setFillColor(240, 240, 240);
    doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('PERFORMANCE DISTRIBUTION', 15, yPosition + 7);
    yPosition += 15;
    const ranges = [
        { label: 'Excellent (90-100%)', min: 90, max: 100 },
        { label: 'Good (75-89%)', min: 75, max: 89 },
        { label: 'Average (50-74%)', min: 50, max: 74 },
        { label: 'Below Average (35-49%)', min: 35, max: 49 },
        { label: 'Poor (<35%)', min: 0, max: 34 }
    ];
    const distributionData = ranges.map(range => {
        const count = students.filter(s => s.averageScore >= range.min && s.averageScore <= range.max).length;
        const percentage = students.length > 0 ? (count / students.length) * 100 : 0;
        return [
            range.label,
            count.toString(),
            `${percentage.toFixed(1)}%`
        ];
    });
    (0, jspdf_autotable_1.default)(doc, {
        startY: yPosition,
        head: [['Performance Range', 'Students', 'Percentage']],
        body: distributionData,
        theme: 'grid',
        headStyles: {
            fillColor: [52, 152, 219],
            textColor: 255,
            fontStyle: 'bold',
            fontSize: 10
        },
        bodyStyles: { fontSize: 9 },
        columnStyles: {
            0: { cellWidth: 90 },
            1: { cellWidth: 40, halign: 'center' },
            2: { cellWidth: 47, halign: 'right' }
        }
    });
    yPosition = doc.lastAutoTable.finalY + 15;
    // Attendance Distribution
    doc.setFillColor(240, 240, 240);
    doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('ATTENDANCE DISTRIBUTION', 15, yPosition + 7);
    yPosition += 15;
    const attRanges = [
        { label: 'Excellent (90-100%)', min: 90, max: 100 },
        { label: 'Good (75-89%)', min: 75, max: 89 },
        { label: 'Satisfactory (60-74%)', min: 60, max: 74 },
        { label: 'Poor (<60%)', min: 0, max: 59 }
    ];
    const attDistributionData = attRanges.map(range => {
        const count = students.filter(s => s.attendancePercentage >= range.min && s.attendancePercentage <= range.max).length;
        const percentage = students.length > 0 ? (count / students.length) * 100 : 0;
        return [
            range.label,
            count.toString(),
            `${percentage.toFixed(1)}%`
        ];
    });
    (0, jspdf_autotable_1.default)(doc, {
        startY: yPosition,
        head: [['Attendance Range', 'Students', 'Percentage']],
        body: attDistributionData,
        theme: 'grid',
        headStyles: {
            fillColor: [52, 152, 219],
            textColor: 255,
            fontStyle: 'bold',
            fontSize: 10
        },
        bodyStyles: { fontSize: 9 },
        columnStyles: {
            0: { cellWidth: 90 },
            1: { cellWidth: 40, halign: 'center' },
            2: { cellWidth: 47, halign: 'right' }
        }
    });
    yPosition = doc.lastAutoTable.finalY + 15;
    // Key Insights
    doc.setFillColor(240, 240, 240);
    doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('KEY INSIGHTS', 15, yPosition + 7);
    yPosition += 15;
    const avgScore = students.reduce((sum, s) => sum + s.averageScore, 0) / students.length;
    const avgAttendance = students.reduce((sum, s) => sum + s.attendancePercentage, 0) / students.length;
    const atRiskCount = students.filter(s => s.averageScore < 40).length;
    const excellentCount = students.filter(s => s.averageScore >= 90).length;
    const insights = [
        `Batch Average Score: ${avgScore.toFixed(2)}%`,
        `Batch Average Attendance: ${avgAttendance.toFixed(2)}%`,
        `Students At Risk (< 40%): ${atRiskCount} (${((atRiskCount / students.length) * 100).toFixed(1)}%)`,
        `Excellent Performers (≥ 90%): ${excellentCount} (${((excellentCount / students.length) * 100).toFixed(1)}%)`,
        `Total Students: ${students.length}`
    ];
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    insights.forEach((insight) => {
        doc.text(`• ${insight}`, 15, yPosition);
        yPosition += 7;
    });
    // Footer
    renderPDFFooter(doc, pageWidth, pageHeight);
    const filename = `${options.batchName.replace(/\s+/g, '_')}_Batch_Report_Analytics_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
};
/**
 * BATCH PRESET 4: PTM Dossiers - Complete individual dossier for each student with page breaks
 * Perfect for printing and distributing to parents during Parent-Teacher Meetings
 */
const generateBatchPTMDossiersPDF = (students, options) => {
    const doc = new jspdf_1.default();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    students.forEach((student, studentIndex) => {
        let yPosition = 20;
        // Clean header with student info
        doc.setFillColor(41, 128, 185);
        doc.rect(0, 0, pageWidth, 40, 'F');
        // Small logo placeholder (top left corner - minimal and subtle)
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(0.5);
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(10, 8, 18, 18, 2, 2);
        doc.setFontSize(5);
        doc.setTextColor(41, 128, 185);
        doc.setFont('helvetica', 'bold');
        doc.text('LOGO', 19, 17.5, { align: 'center' });
        // Student name and info
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(student.StudentInfo.FullName, pageWidth / 2, 14, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`${options.batchName} • ${options.courseName}`, pageWidth / 2, 23, { align: 'center' });
        doc.setFontSize(8);
        doc.text(`${student.StudentInfo.SchoolName || ''} • ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} • ${student.StudentInfo.YearName || 'N/A'}`, pageWidth / 2, 32, { align: 'center' });
        yPosition = 50;
        // Performance Summary Section (directly after header, no redundant info block)
        doc.setTextColor(0, 0, 0);
        doc.setFillColor(240, 240, 240);
        doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('PERFORMANCE SUMMARY', 15, yPosition + 7);
        yPosition += 15;
        const minTestsForRanking = options.filters?.minTestsForRanking || 5;
        const metrics = [
            {
                label: 'Class Rank',
                value: getRankDisplay(student, students.length, minTestsForRanking),
                color: getRankColor(student.rank, students.length)
            },
            {
                label: 'Average Score',
                value: `${student.averageScore.toFixed(2)}%`,
                color: getScoreColor(student.averageScore)
            },
            {
                label: 'Attendance',
                value: `${student.attendancePercentage.toFixed(2)}%`,
                color: getAttendanceColor(student.attendancePercentage)
            },
        ];
        const boxWidth = (pageWidth - 35) / 3;
        let xPos = 15;
        metrics.forEach((metric) => {
            doc.setFillColor(...metric.color);
            doc.roundedRect(xPos, yPosition, boxWidth, 20, 2, 2, 'F');
            const isLightBackground = metric.color[0] > 200 && metric.color[1] > 200;
            doc.setTextColor(isLightBackground ? 0 : 255, isLightBackground ? 0 : 255, isLightBackground ? 0 : 255);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.text(metric.label, xPos + boxWidth / 2, yPosition + 7, { align: 'center' });
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(metric.value, xPos + boxWidth / 2, yPosition + 15, { align: 'center' });
            xPos += boxWidth + 2.5;
        });
        yPosition += 30;
        doc.setTextColor(0, 0, 0);
        // Test Results Section - Grouped by Category
        if (student.TestInfo && student.TestInfo.length > 0) {
            doc.setFillColor(240, 240, 240);
            doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('TEST RESULTS BY CATEGORY', 15, yPosition + 7);
            yPosition += 15;
            // Group tests by category
            const testsByCategory = {};
            student.TestInfo.forEach((test) => {
                const category = test.testCategoryName || 'Uncategorized';
                if (!testsByCategory[category]) {
                    testsByCategory[category] = [];
                }
                testsByCategory[category].push(test);
            });
            // Render each category
            Object.entries(testsByCategory).forEach(([category, tests]) => {
                // Check if we need a new page
                if (yPosition > pageHeight - 80) {
                    doc.addPage();
                    yPosition = 20;
                }
                // Category header
                doc.setFillColor(52, 152, 219);
                doc.rect(10, yPosition, pageWidth - 20, 8, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(10);
                doc.setFont('helvetica', 'bold');
                doc.text(`${category} (${tests.length} test${tests.length > 1 ? 's' : ''})`, 15, yPosition + 5.5);
                yPosition += 10;
                // Calculate category average
                const categoryAverage = tests.reduce((sum, test) => {
                    return sum + (test.totalScored / test.totalMaxMarks) * 100;
                }, 0) / tests.length;
                // Test data for this category
                const testData = tests.map((test, index) => {
                    const percentage = (test.totalScored / test.totalMaxMarks) * 100;
                    return [
                        (index + 1).toString(),
                        test.testName || 'N/A',
                        `${test.totalScored.toFixed(1)}`,
                        `${test.totalMaxMarks}`,
                        `${percentage.toFixed(2)}%`,
                        getPerformanceLabel(percentage)
                    ];
                });
                // Add category average row
                testData.push([
                    '',
                    'Category Average',
                    '',
                    '',
                    `${categoryAverage.toFixed(2)}%`,
                    getPerformanceLabel(categoryAverage)
                ]);
                (0, jspdf_autotable_1.default)(doc, {
                    startY: yPosition,
                    head: [['#', 'Test Name', 'Scored', 'Max', 'Percentage', 'Performance']],
                    body: testData,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [71, 171, 238],
                        textColor: 255,
                        fontStyle: 'bold',
                        fontSize: 9
                    },
                    bodyStyles: { fontSize: 8 },
                    columnStyles: {
                        0: { cellWidth: 12, halign: 'center' },
                        1: { cellWidth: 65 },
                        2: { cellWidth: 20, halign: 'right' },
                        3: { cellWidth: 20, halign: 'right' },
                        4: { cellWidth: 25, halign: 'right' },
                        5: { cellWidth: 30, halign: 'center' },
                    },
                    alternateRowStyles: { fillColor: [245, 245, 245] },
                    didParseCell: (data) => {
                        // Highlight category average row
                        if (data.section === 'body' && data.row.index === testData.length - 1) {
                            data.cell.styles.fillColor = [230, 245, 255];
                            data.cell.styles.fontStyle = 'bold';
                            data.cell.styles.textColor = [52, 152, 219];
                        }
                        // Color code performance
                        if (data.section === 'body' && data.column.index === 5) {
                            const percentage = parseFloat(testData[data.row.index][4]);
                            if (percentage >= 75) {
                                data.cell.styles.textColor = [16, 185, 129];
                                data.cell.styles.fontStyle = 'bold';
                            }
                            else if (percentage < 50) {
                                data.cell.styles.textColor = [239, 68, 68];
                                data.cell.styles.fontStyle = 'bold';
                            }
                        }
                    }
                });
                yPosition = doc.lastAutoTable.finalY + 8;
            });
        }
        // Attendance Section
        if (student.AttendanceInfo && student.AttendanceInfo.length > 0) {
            // Check if we need a new page
            if (yPosition > pageHeight - 80) {
                doc.addPage();
                yPosition = 20;
            }
            doc.setFillColor(240, 240, 240);
            doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            doc.text('ATTENDANCE RECORD', 15, yPosition + 7);
            yPosition += 15;
            // Group attendance by topic and sum totals
            const attendanceByTopic = student.AttendanceInfo.reduce((acc, att) => {
                const topicName = att.TopicName || 'N/A';
                if (!acc[topicName]) {
                    acc[topicName] = {
                        TopicName: topicName,
                        Presents: 0,
                        Absents: 0,
                        TotalClasses: 0
                    };
                }
                acc[topicName].Presents += att.Presents;
                acc[topicName].Absents += att.Absents;
                acc[topicName].TotalClasses += att.TotalClasses;
                return acc;
            }, {});
            const attendanceData = Object.values(attendanceByTopic).map((att, index) => {
                const percentage = att.TotalClasses > 0 ? (att.Presents / att.TotalClasses) * 100 : 0;
                return [
                    (index + 1).toString(),
                    att.TopicName,
                    att.Presents.toString(),
                    att.Absents.toString(),
                    att.TotalClasses.toString(),
                    `${percentage.toFixed(2)}%`,
                    getAttendanceLabel(percentage)
                ];
            });
            (0, jspdf_autotable_1.default)(doc, {
                startY: yPosition,
                head: [['#', 'Topic', 'Present', 'Absent', 'Total', 'Percentage', 'Status']],
                body: attendanceData,
                theme: 'grid',
                headStyles: {
                    fillColor: [52, 152, 219],
                    textColor: 255,
                    fontStyle: 'bold',
                    fontSize: 9
                },
                bodyStyles: { fontSize: 8 },
                columnStyles: {
                    0: { cellWidth: 10, halign: 'center' },
                    1: { cellWidth: 50 },
                    2: { cellWidth: 20, halign: 'center' },
                    3: { cellWidth: 20, halign: 'center' },
                    4: { cellWidth: 20, halign: 'center' },
                    5: { cellWidth: 25, halign: 'right' },
                    6: { cellWidth: 30, halign: 'center' },
                },
                alternateRowStyles: { fillColor: [245, 245, 245] },
                didParseCell: (data) => {
                    if (data.section === 'body' && data.column.index === 6) {
                        const percentage = parseFloat(attendanceData[data.row.index][5]);
                        if (percentage >= 75) {
                            data.cell.styles.textColor = [16, 185, 129];
                            data.cell.styles.fontStyle = 'bold';
                        }
                        else if (percentage < 60) {
                            data.cell.styles.textColor = [239, 68, 68];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                }
            });
            yPosition = doc.lastAutoTable.finalY + 10;
        }
        // Teacher's Comments Section (only if includeComments is true)
        const includeComments = options.filters?.includeComments !== false; // Default to true
        if (includeComments) {
            if (yPosition > pageHeight - 60) {
                doc.addPage();
                yPosition = 20;
            }
            doc.setFillColor(240, 240, 240);
            doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            doc.text('TEACHER\'S COMMENTS & RECOMMENDATIONS', 15, yPosition + 7);
            yPosition += 15;
            // Generate performance-based comments
            const comments = [];
            // Overall performance comment
            if (student.averageScore >= 90) {
                comments.push('• Excellent overall academic performance! Continue the outstanding work.');
            }
            else if (student.averageScore >= 75) {
                comments.push('• Good overall academic performance. Keep up the consistent effort.');
            }
            else if (student.averageScore >= 50) {
                comments.push('• Average overall performance. Consistent effort and focus can lead to improvement.');
            }
            else {
                comments.push('• Overall performance requires immediate attention. Recommend personalized tutoring and regular follow-ups.');
            }
            // Category-specific analysis
            if (student.TestInfo && student.TestInfo.length > 0) {
                const testsByCategory = {};
                student.TestInfo.forEach((test) => {
                    const category = test.testCategoryName || 'Uncategorized';
                    if (!testsByCategory[category]) {
                        testsByCategory[category] = [];
                    }
                    testsByCategory[category].push(test);
                });
                const categoryAverages = [];
                Object.entries(testsByCategory).forEach(([category, tests]) => {
                    const categoryAverage = tests.reduce((sum, test) => {
                        return sum + (test.totalScored / test.totalMaxMarks) * 100;
                    }, 0) / tests.length;
                    categoryAverages.push({ category, average: categoryAverage });
                });
                // Sort to find strongest and weakest categories
                categoryAverages.sort((a, b) => b.average - a.average);
                if (categoryAverages.length > 0) {
                    const strongest = categoryAverages[0];
                    const weakest = categoryAverages[categoryAverages.length - 1];
                    // Highlight strong category
                    if (strongest.average >= 80) {
                        comments.push(`• Strong performance in ${strongest.category} (${strongest.average.toFixed(1)}%). Excellent work!`);
                    }
                    // Highlight weak category if significantly lower
                    if (weakest.average < 60 && categoryAverages.length > 1) {
                        comments.push(`• ${weakest.category} needs attention (${weakest.average.toFixed(1)}%). Recommend additional practice and support.`);
                    }
                    else if (weakest.average < 75 && categoryAverages.length > 1 && strongest.average - weakest.average > 20) {
                        comments.push(`• Consider focusing more on ${weakest.category} (${weakest.average.toFixed(1)}%) to balance performance across categories.`);
                    }
                }
            }
            // Attendance comment
            if (student.attendancePercentage >= 90) {
                comments.push('• Excellent attendance record. Regular presence is key to success.');
            }
            else if (student.attendancePercentage >= 75) {
                comments.push('• Good attendance with occasional absences. Try to maintain consistency.');
            }
            else {
                comments.push('• Poor attendance is affecting academic performance. Please ensure regular attendance.');
            }
            // Rank-based comment
            if (student.rank <= Math.ceil(students.length * 0.1)) {
                comments.push(`• Currently ranked in the top 10% of the batch (Rank ${student.rank}). Outstanding achievement!`);
            }
            else if (student.rank <= Math.ceil(students.length * 0.25)) {
                comments.push(`• Ranked in the top 25% of the batch (Rank ${student.rank}). Great progress!`);
            }
            else if (student.averageScore < 40) {
                comments.push('• Student is at risk. Recommend immediate intervention and support.');
            }
            // Comment box
            doc.setDrawColor(200, 200, 200);
            doc.roundedRect(10, yPosition, pageWidth - 20, 45, 2, 2);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            comments.forEach((comment) => {
                doc.text(comment, 15, yPosition + 8);
                yPosition += 7;
            });
            yPosition = yPosition - comments.length * 7 + 50;
            // Signature section
            yPosition += 10;
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.text('Teacher\'s Signature: ___________________', 15, yPosition);
            doc.text('Date: ___________________', pageWidth - 75, yPosition);
            yPosition += 15;
            doc.text('Parent\'s Signature: ___________________', 15, yPosition);
            doc.text('Date: ___________________', pageWidth - 75, yPosition);
        } // End of includeComments block
        // Footer for this student's pages - simpler, non-cluttered version
        const currentPageCount = doc.internal.getNumberOfPages();
        const studentPageStart = studentIndex === 0 ? 1 : currentPageCount - Math.floor((currentPageCount - 1) / students.length) + 1;
        // Add footer to all pages for this student
        for (let pageNum = studentPageStart; pageNum <= currentPageCount; pageNum++) {
            doc.setPage(pageNum);
            doc.setFontSize(8);
            doc.setTextColor(128, 128, 128);
            // Center the student name and page number
            const pageLabel = `${student.StudentInfo.FullName} - Page ${pageNum - studentPageStart + 1}`;
            doc.text(pageLabel, pageWidth / 2, pageHeight - 10, { align: 'center' });
        }
        // Add page break after each student (except the last one)
        if (studentIndex < students.length - 1) {
            doc.addPage();
        }
    });
    const filename = `${options.batchName.replace(/\s+/g, '_')}_PTM_Dossiers_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
};
// Helper function to estimate pages needed per student
const getPagesForStudent = (student) => {
    let pages = 1; // Base page
    // Add pages for tests
    if (student.TestInfo && student.TestInfo.length > 0) {
        const testsByCategory = {};
        student.TestInfo.forEach((test) => {
            const category = test.testCategoryName || 'Uncategorized';
            if (!testsByCategory[category]) {
                testsByCategory[category] = [];
            }
            testsByCategory[category].push(test);
        });
        const categoryCount = Object.keys(testsByCategory).length;
        pages += Math.ceil(categoryCount / 2); // Estimate 2 categories per page
    }
    // Add page for attendance if needed
    if (student.AttendanceInfo && student.AttendanceInfo.length > 5) {
        pages += 1;
    }
    return pages;
};
// ========== DATABASE TEMPLATE PDF GENERATION (SERVER-SIDE) ==========
/**
 * Generate PDF from database template by calling backend API
 */
const generateReportFromDatabaseTemplate = async (templateId, students, options) => {
    try {
        console.log(`[generateReportFromDatabaseTemplate] Generating PDF with template ID: ${templateId}`);
        // Get auth headers
        const currentUser = localStorage.getItem('currentUser');
        let token = '';
        if (currentUser && currentUser !== 'undefined') {
            try {
                const user = JSON.parse(currentUser);
                token = user.token || '';
            }
            catch (error) {
                console.error('Failed to parse currentUser:', error);
            }
        }
        // Call backend API
        const API_BASE_URL = (0, config_1.getConfig)().nodeSiteUrl;
        const response = await fetch(`${API_BASE_URL}v1/reports/generate-from-template`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
                templateId: parseInt(templateId),
                students: students
            })
        });
        if (!response.ok) {
            const errorText = await response.text();
            console.error('[generateReportFromDatabaseTemplate] Error response:', errorText);
            throw new Error(`Failed to generate report from template: ${response.statusText}`);
        }
        // Download the PDF
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report-template-${templateId}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        console.log(`[generateReportFromDatabaseTemplate] PDF downloaded successfully`);
    }
    catch (error) {
        console.error('[generateReportFromDatabaseTemplate] Error generating PDF:', error);
        alert(`Failed to generate PDF: ${error instanceof Error ? error.message : String(error)}`);
        throw error;
    }
};
// ========== GRADE CARD PDF GENERATION (SERVER-SIDE) ==========
/**
 * Generate grade card PDF by calling backend API
 * Converts student data to grade card format and sends to backend
 */
const generateGradeCardPDF = async (type, students, options) => {
    try {
        // Map student data to grade card format
        const gradeCardType = type === 'pre-primary-grade' ? 'pre-primary' : 'school';
        const gradeCardStudents = students.map(student => mapStudentToGradeCard(student, gradeCardType));
        // Get auth headers
        const currentUser = localStorage.getItem('currentUser');
        let token = '';
        if (currentUser && currentUser !== 'undefined') {
            try {
                const user = JSON.parse(currentUser);
                token = user.token || '';
            }
            catch (error) {
                console.error('Failed to parse currentUser:', error);
            }
        }
        // Call backend API
        const API_BASE_URL = (0, config_1.getConfig)().nodeSiteUrl;
        const response = await fetch(`${API_BASE_URL}v1/reports/grade-card`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
                templateCode: gradeCardType,
                students: gradeCardStudents
            })
        });
        if (!response.ok) {
            throw new Error(`Failed to generate grade card: ${response.statusText}`);
        }
        // Download the PDF
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${options.batchName.replace(/\s+/g, '_')}_Grade_Cards_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }
    catch (error) {
        console.error('Error generating grade card PDF:', error);
        alert(`Failed to generate grade card PDF: ${error instanceof Error ? error.message : String(error)}`);
    }
};
/**
 * Map StudentWithScore to Grade Card format
 * Fills missing data with placeholders
 */
const mapStudentToGradeCard = (student, type) => {
    // Use 'any' type since we're accessing properties that may not exist in the interface
    // This is intentional as we're mapping to a different structure with fallbacks
    const studentInfo = student.StudentInfo || {};
    // Common fields
    const commonData = {
        studentName: studentInfo.FullName || 'Data Missing',
        dateOfBirth: studentInfo.DateOfBirth || 'Data Missing',
        className: studentInfo.BatchName || 'Data Missing',
        schoolLogo: '',
    };
    if (type === 'pre-primary') {
        // Pre-Primary (Senior KG) format
        return {
            ...commonData,
            studyCentre: studentInfo.BatchName || 'Data Missing',
            section: studentInfo.Section || 'A',
            dateOfAdmission: studentInfo.DateOfAdmission || 'Data Missing',
            admissionNo: studentInfo.AdmissionNo || studentInfo.EnquiryId?.toString() || 'Data Missing',
            motherName: studentInfo.MotherName || 'Data Missing',
            fatherName: studentInfo.FatherName || 'Data Missing',
            address: studentInfo.Address || 'Data Missing',
            phoneNo: studentInfo.Mobile || 'Data Missing',
            mascotImage: '',
            // Skills arrays - with default placeholders
            literacySkills: Array(11).fill(0).map((_, i) => ({
                description: `Literacy skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            literacyOralTermI: 'Data Missing',
            literacyOralTermII: 'Data Missing',
            literacyWrittenTermI: 'Data Missing',
            literacyWrittenTermII: 'Data Missing',
            numeracySkills: Array(7).fill(0).map((_, i) => ({
                description: `Numeracy skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            numeracyOralTermI: 'Data Missing',
            numeracyOralTermII: 'Data Missing',
            numeracyWrittenTermI: 'Data Missing',
            numeracyWrittenTermII: 'Data Missing',
            generalAwarenessSkills: Array(8).fill(0).map((_, i) => ({
                description: `General awareness skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            gaOralTermI: 'Data Missing',
            gaOralTermII: 'Data Missing',
            gaWrittenTermI: 'Data Missing',
            gaWrittenTermII: 'Data Missing',
            rhymesSkills: Array(3).fill(0).map((_, i) => ({
                description: `Rhymes skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            storySkills: Array(2).fill(0).map((_, i) => ({
                description: `Story skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            envScienceSkills: Array(3).fill(0).map((_, i) => ({
                description: `Environmental science skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            artCraftSkills: Array(6).fill(0).map((_, i) => ({
                description: `Art and craft skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            motorPhysicalSkills: Array(5).fill(0).map((_, i) => ({
                description: `Motor/physical skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            shlokaSkills: Array(2).fill(0).map((_, i) => ({
                description: `Shloka/prayer skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            rhymesMotorSkills: Array(2).fill(0).map((_, i) => ({
                description: `Rhymes motor skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            sportsYogaSkills: Array(3).fill(0).map((_, i) => ({
                description: `Sports/yoga skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            computerSkills: Array(6).fill(0).map((_, i) => ({
                description: `Computer skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            socialEmotionalSkills: Array(12).fill(0).map((_, i) => ({
                description: `Social/emotional skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            grossMotorSkills: Array(4).fill(0).map((_, i) => ({
                description: `Gross motor skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            fineMotorSkills: Array(3).fill(0).map((_, i) => ({
                description: `Fine motor skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            colorsShapesSkills: Array(2).fill(0).map((_, i) => ({
                description: `Colors and shapes skill ${i + 1}`,
                termI: 'Data Missing',
                termII: 'Data Missing'
            })),
            // Co-curricular
            gamesTermI: 'Data Missing',
            gamesTermII: 'Data Missing',
            musicTermI: 'Data Missing',
            musicTermII: 'Data Missing',
            danceTermI: 'Data Missing',
            danceTermII: 'Data Missing',
            // Health & Attendance
            heightTermI: 'Data Missing',
            heightTermII: 'Data Missing',
            weightTermI: 'Data Missing',
            weightTermII: 'Data Missing',
            oralHygieneTermI: 'Data Missing',
            oralHygieneTermII: 'Data Missing',
            attendanceTermI: student.AttendanceInfo?.[0]?.Presents?.toString() || 'Data Missing',
            attendanceTermII: 'Data Missing',
            workingDaysTermI: student.AttendanceInfo?.[0]?.TotalClasses?.toString() || 'Data Missing',
            workingDaysTermII: 'Data Missing',
            // Remarks
            generalRemarks: 'Data Missing'
        };
    }
    else {
        // School (Class IV-VIII+) format
        const academicSession = new Date().getFullYear() + '-' + (new Date().getFullYear() + 1);
        // Try to extract subject data from test info
        const subjects = student.TestInfo?.slice(0, 6).map((test, i) => {
            const percentage = test.totalMaxMarks > 0 ? (test.totalScored / test.totalMaxMarks) * 100 : 0;
            return {
                name: test.testCategoryName || `Subject ${i + 1}`,
                tla1: 'Data Missing',
                notebook: 'Data Missing',
                enrichment: 'Data Missing',
                activity: 'Data Missing',
                tla2: test.totalScored?.toString() || 'Data Missing',
                marksObtained: test.totalScored?.toString() || 'Data Missing',
                grade: percentage >= 90 ? 'A1' :
                    percentage >= 80 ? 'A2' :
                        percentage >= 70 ? 'B1' : 'Data Missing'
            };
        }) || Array(6).fill(0).map((_, i) => ({
            name: `Subject ${i + 1}`,
            tla1: 'Data Missing',
            notebook: 'Data Missing',
            enrichment: 'Data Missing',
            activity: 'Data Missing',
            tla2: 'Data Missing',
            marksObtained: 'Data Missing',
            grade: 'Data Missing'
        }));
        const totalPresents = student.AttendanceInfo?.reduce((sum, att) => sum + (att.Presents || 0), 0) || 0;
        const totalClasses = student.AttendanceInfo?.reduce((sum, att) => sum + (att.TotalClasses || 0), 0) || 0;
        return {
            ...commonData,
            academicSession,
            rollNo: studentInfo.RollNo?.toString() || studentInfo.EnquiryId?.toString() || 'Data Missing',
            subjects,
            overallMarks: student.TestInfo?.reduce((sum, test) => sum + (test.totalScored || 0), 0).toString() || 'Data Missing',
            percentage: student.averageScore?.toFixed(1) || 'Data Missing',
            overallGrade: student.averageScore >= 90 ? 'A1' :
                student.averageScore >= 80 ? 'A2' :
                    student.averageScore >= 70 ? 'B1' : 'Data Missing',
            height: 'Data Missing',
            weight: 'Data Missing',
            artEducation: 'Data Missing',
            physicalEducation: 'Data Missing',
            computerEducation: 'Data Missing',
            skills: [
                { name: 'THINKING SKILLS', grade: 'Data Missing', indicator: 'Data Missing' },
                { name: 'SOCIAL SKILLS', grade: 'Data Missing', indicator: 'Data Missing' },
                { name: 'EMOTIONAL SKILLS', grade: 'Data Missing', indicator: 'Data Missing' }
            ],
            attitudes: [
                { name: 'TEACHERS', grade: 'Data Missing', indicator: 'Data Missing' },
                { name: 'SCHOOL MATES', grade: 'Data Missing', indicator: 'Data Missing' },
                { name: 'ENVIRONMENT', grade: 'Data Missing', indicator: 'Data Missing' },
                { name: 'VALUE SYSTEM', grade: 'Data Missing', indicator: 'Data Missing' }
            ],
            activities: [
                { name: 'LITERARY SKILLS', grade: 'Data Missing', indicator: 'Data Missing' },
                { name: 'STEM SKILLS', grade: 'Data Missing', indicator: 'Data Missing' }
            ],
            attendancePresent: totalPresents.toString() || 'Data Missing',
            attendanceTotal: totalClasses.toString() || 'Data Missing',
            classTeacherRemarks: 'Data Missing'
        };
    }
};
